import { useEffect, useLayoutEffect, useState, type ReactNode } from "react";
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  Icon,
  IconButton,
  Image,
  Show,
  Text,
  useDisclosure,
  useMediaQuery,
} from "@chakra-ui/react";
import { useLocation, useParams } from "react-router-dom";
import HamburgerMenuIcon from "assets/icons/medium/hamburger-menu.svg?react";
import poweredByRexHubLogoV2 from "assets/powered-by-rex-hub-v2.svg";
import { AgencyLogo } from "components/agency-logo";
import { DeveloperControls } from "components/developer-controls";
import {
  PREVIEW_BANNER_HEIGHT,
  PreviewBanner,
} from "components/preview-banner";
import { useAuth } from "contexts/auth-provider";

const TOP_NAVIGATION_BAR_HEIGHT = 16;

let topNavigationBarHeight = TOP_NAVIGATION_BAR_HEIGHT;

export const getTopNavigationBarHeight = () => {
  return topNavigationBarHeight;
};

type TopNavigationBarProps = {
  leftNavigationMenu: ReactNode;
  showPreviewBanner?: boolean;
};
export function TopNavigationBar({
  leftNavigationMenu,
  showPreviewBanner,
}: TopNavigationBarProps) {
  const isDev = import.meta.env.DEV;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { user } = useAuth();
  const { propertyId } = useParams();
  const [currentLocation, setCurrentLocation] = useState<string>();
  const location = useLocation();
  const property = user?.related?.listings?.find(
    (property) => property.remote_record_id === propertyId,
  );

  const address = property?.address.short_address;

  const [isPrint] = useMediaQuery("print");

  useEffect(() => {
    if (currentLocation !== location.pathname) {
      setCurrentLocation(location.pathname);
      onClose();
    }
  }, [currentLocation, location.pathname, setCurrentLocation, onClose]);

  useLayoutEffect(() => {
    if (showPreviewBanner) {
      topNavigationBarHeight =
        TOP_NAVIGATION_BAR_HEIGHT + PREVIEW_BANNER_HEIGHT;
    } else {
      topNavigationBarHeight = TOP_NAVIGATION_BAR_HEIGHT;
    }
  }, [showPreviewBanner]);

  if (isPrint) {
    return null;
  }

  return (
    <>
      {showPreviewBanner && <PreviewBanner />}
      <Box
        h={TOP_NAVIGATION_BAR_HEIGHT}
        flex={1}
        w={"100%"}
        bg={"gray.900"}
        pos={"fixed"}
        top={showPreviewBanner ? PREVIEW_BANNER_HEIGHT : 0}
        left={0}
        zIndex={1}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
        p={6}
      >
        <AgencyLogo />
        <Show above={"lg"}>
          <Box display={"flex"} flexDir={"row"} alignItems={"center"} gap={4}>
            {isDev && <DeveloperControls />}
            <Image
              ml={"auto"}
              src={poweredByRexHubLogoV2}
              alt={"Powered By Rex Hub"}
              w={"140px"}
              h={"20px"}
              flexGrow={0}
            />
          </Box>
        </Show>
        <Show below={"lg"}>
          <IconButton
            variant={"ghost"}
            colorScheme={"white"}
            aria-label={"Menu Button"}
            fontSize={"20px"}
            onClick={onOpen}
            icon={<Icon as={HamburgerMenuIcon} color={"white"} />}
          />
        </Show>
      </Box>
      <Drawer onClose={onClose} isOpen={isOpen} size={"full"}>
        <DrawerContent bg={"client.navigation.background"}>
          <DrawerCloseButton
            size={"lg"}
            color={"client.navigation.text.primary"}
          />

          <DrawerHeader
            borderBottomWidth={address && "1px"}
            borderBottomColor={"client.navigation.text.secondary"}
          >
            <Text
              fontSize={"2xl"}
              fontWeight={700}
              color={"client.navigation.text.primary"}
            >
              {address}
            </Text>
          </DrawerHeader>
          <DrawerBody p={0}>{leftNavigationMenu}</DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}
